import type { ValidationErrors } from '../../../../../../utils/validation';
import type { IDiagnosis } from '../../../../../interfaces/diagnosis';
import { nullDiagnosis } from '../../../../../../../common/validation/diagnosis';
import { translateKey } from '../../../../../../translate';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { Icd10Selection, NOABNORMALITY, NODIAGNOSISYET } from './Icd10Selection';

interface IIcd10SelectionProps {
    bold?: boolean;
    diagnosis: IDiagnosis;
    errors: ValidationErrors;
    hideWorkingDiagnosis?: boolean;
    hideWorkingDiagnosisLabel?: boolean;
    isRequired?: boolean;
    isWorkingDiagnosis?: boolean;
    onChange: (diagnosis: IDiagnosis) => Promise<void>;
    onDoValidate: () => Promise<void>;
    placeholder?: string;
    showOnlyWorkingDiagnosis?: boolean;
    specialismCode: string;
}

export function Icd10Selections(props: IIcd10SelectionProps) {
    return (
        <>
            {props.hideWorkingDiagnosis ? null : (
                <HorizontalFormGroup hasErrors={props.errors.has('diagnosis.diagnosisConsiderationIcd10')}>
                    {props.hideWorkingDiagnosisLabel ? null : (
                        <FormLabel isRequired={props.isRequired}>
                            {translateKey('diagnosis.workingDiagnosis')}
                        </FormLabel>
                    )}
                    <InputContainer>
                        <Icd10Selection
                            bold={props.bold}
                            error={
                                props.errors.get('diagnosis.diagnosisConsiderationIcd10') ||
                                props.errors.get('customControlData.optometrische_diagnose')
                            }
                            icd10Code={props.diagnosis.diagnosisConsiderationIcd10}
                            id="working-diagnosis"
                            isWorkingDiagnosis={props.isWorkingDiagnosis === false ? false : true}
                            onChange={async (icd10Code, thesaurusTermId, icd10CodeFallBack) => {
                                if (icd10CodeFallBack) {
                                    await props.onChange({
                                        ...props.diagnosis,
                                        diagnosisConsiderationIcd10fallback: icd10CodeFallBack,
                                    });
                                } else if (!icd10Code || noWorkingDiagnosis(icd10Code)) {
                                    await props.onChange({
                                        ...nullDiagnosis,
                                        diagnosisConsiderationIcd10: icd10Code,
                                        considerationThesaurusTermId: thesaurusTermId,
                                    });
                                } else {
                                    await props.onChange({
                                        ...props.diagnosis,
                                        diagnosisConsiderationIcd10: icd10Code,
                                        considerationThesaurusTermId: thesaurusTermId,
                                    });
                                }
                            }}
                            onDoValidate={async () => await props.onDoValidate()}
                            placeholder={
                                props.placeholder ? props.placeholder : translateKey('diagnosis.selectOrTypeATerm')
                            }
                            specialismCode={props.specialismCode}
                            termId={props.diagnosis.considerationThesaurusTermId}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            )}
            {!props.showOnlyWorkingDiagnosis &&
                ((props.diagnosis.diagnosisConsiderationIcd10 &&
                    !noWorkingDiagnosis(props.diagnosis.diagnosisConsiderationIcd10)) ||
                    props.hideWorkingDiagnosis) && (
                    <HorizontalFormGroup
                        hasErrors={props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10a')}
                    >
                        <FormLabel>{translateKey('diagnosis.differentialDiagnosis')}</FormLabel>
                        <InputContainer>
                            <Icd10Selection
                                bold={props.bold}
                                error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10a')}
                                icd10Code={props.diagnosis.diagnosisDifferentialConsiderationIcd10a}
                                id="differential-diagnosis-0"
                                isWorkingDiagnosis={props.isWorkingDiagnosis === false ? false : true}
                                onChange={async (icd10Code, thesaurusTermId) => {
                                    if (!icd10Code || noWorkingDiagnosis(icd10Code)) {
                                        await props.onChange({
                                            ...props.diagnosis,
                                            diagnosisDifferentialConsiderationIcd10a: icd10Code,
                                            diagnosisDifferentialConsiderationIcd10b: null,
                                            diagnosisDifferentialConsiderationIcd10c: null,
                                            differentialConsiderationThesaurusTermIda: thesaurusTermId,
                                            differentialConsiderationThesaurusTermIdb: null,
                                            differentialConsiderationThesaurusTermIdc: null,
                                        });
                                    } else {
                                        await props.onChange({
                                            ...props.diagnosis,
                                            diagnosisDifferentialConsiderationIcd10a: icd10Code,
                                            differentialConsiderationThesaurusTermIda: thesaurusTermId,
                                        });
                                    }
                                }}
                                onDoValidate={async () => await props.onDoValidate()}
                                placeholder={
                                    props.placeholder ? props.placeholder : translateKey('diagnosis.selectOrTypeATerm')
                                }
                                specialismCode={props.specialismCode}
                                termId={props.diagnosis.differentialConsiderationThesaurusTermIda}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
            {!props.showOnlyWorkingDiagnosis &&
                props.diagnosis.diagnosisDifferentialConsiderationIcd10a &&
                !noWorkingDiagnosis(props.diagnosis.diagnosisDifferentialConsiderationIcd10a) && (
                    <HorizontalFormGroup
                        hasErrors={props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10b')}
                    >
                        <FormLabel>{translateKey('diagnosis.differentialDiagnosis')}</FormLabel>
                        <InputContainer>
                            <Icd10Selection
                                bold={props.bold}
                                error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10b')}
                                icd10Code={props.diagnosis.diagnosisDifferentialConsiderationIcd10b}
                                id="differential-diagnosis-1"
                                onChange={async (icd10Code, thesaurusTermId) => {
                                    if (icd10Code) {
                                        await props.onChange({
                                            ...props.diagnosis,
                                            diagnosisDifferentialConsiderationIcd10b: icd10Code,
                                            differentialConsiderationThesaurusTermIdb: thesaurusTermId,
                                        });
                                    } else {
                                        await props.onChange({
                                            ...props.diagnosis,
                                            diagnosisDifferentialConsiderationIcd10b: null,
                                            diagnosisDifferentialConsiderationIcd10c: null,
                                            differentialConsiderationThesaurusTermIdb: null,
                                            differentialConsiderationThesaurusTermIdc: null,
                                        });
                                    }
                                }}
                                onDoValidate={async () => await props.onDoValidate()}
                                placeholder={
                                    props.placeholder ? props.placeholder : translateKey('diagnosis.selectOrTypeATerm')
                                }
                                specialismCode={props.specialismCode}
                                termId={props.diagnosis.differentialConsiderationThesaurusTermIdb}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
            {!props.showOnlyWorkingDiagnosis && props.diagnosis.diagnosisDifferentialConsiderationIcd10b && (
                <HorizontalFormGroup hasErrors={props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10c')}>
                    <FormLabel>{translateKey('diagnosis.differentialDiagnosis')}</FormLabel>
                    <InputContainer>
                        <Icd10Selection
                            bold={props.bold}
                            error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10c')}
                            icd10Code={props.diagnosis.diagnosisDifferentialConsiderationIcd10c}
                            id="differential-diagnosis-2"
                            onChange={async (icd10Code, thesaurusTermId) =>
                                await props.onChange({
                                    ...props.diagnosis,
                                    diagnosisDifferentialConsiderationIcd10c: icd10Code,
                                    differentialConsiderationThesaurusTermIdc: thesaurusTermId,
                                })
                            }
                            onDoValidate={async () => await props.onDoValidate()}
                            placeholder={
                                props.placeholder ? props.placeholder : translateKey('diagnosis.selectOrTypeATerm')
                            }
                            specialismCode={props.specialismCode}
                            termId={props.diagnosis.differentialConsiderationThesaurusTermIdc}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            )}
        </>
    );

    function noWorkingDiagnosis(diagnosis: string | null) {
        return diagnosis === NODIAGNOSISYET || diagnosis === NOABNORMALITY;
    }
}
